// fetch() with deadlines, for every data read a safety output depends on (depth/sigma tiles, the
// bathymetry manifest, charted hazards).
//
// A bare fetch() has no deadline: one request stalled on flaky marina Wi-Fi never settles, so the
// work waiting on it (the boat's depth window, a chart compute) never finishes and never retries —
// the HUD sat on "Loading depth at the boat…" until a reload. Every read here is bounded three ways:
//   • firstByteMs — no response headers in this long → timed out
//   • idleMs      — the body stops arriving for this long → timed out (a slow link that keeps
//                   delivering bytes is NOT cut off: the clock restarts on every chunk)
//   • totalMs     — hard ceiling on the whole read (a trickle can't hold it open forever)
// A timeout is a FAILED read (FetchFailed 'timeout'), never "no data": callers count it, show it and
// retry. An abort of the CALLER's signal is passed through unchanged (superseded work, not a failure).
//
// DOM-free: used from the UI thread and the compute worker.

export interface Deadline {
  firstByteMs: number;
  idleMs: number;
  totalMs: number;
}

/** Tiles are small (≤ ~130 kB) but many load in parallel over a slow link, so the idle clock (not the
 *  total) is what catches a stall; the total only bounds a pathological trickle. */
export const TILE_DEADLINE: Deadline = { firstByteMs: 15_000, idleMs: 15_000, totalMs: 60_000 };

export type FetchFailReason = 'timeout' | 'network' | 'http' | 'missing' | 'corrupt' | 'invalid' | 'truncated';

/** A failed read's reason in plain words ("server error"): what the screen says (ui/jargonGuard.ts). */
export function failReasonWords(reason: FetchFailReason): string {
  return {
    timeout: 'timed out', network: 'no connection', http: 'server error', missing: 'missing from the published data',
    corrupt: 'corrupt', invalid: 'invalid', truncated: 'cut off',
  }[reason];
}

/** A failure sentence written for the screen. Thrown as a SayError, a display boundary shows it as it
 *  is; any other error's own text ("HTTP 503", "not a PNG", a TypeError) is a log detail. */
export class SayError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SayError';
  }
}

/** What went wrong, in plain words: a SayError's own sentence; a failed read as `fallback` with its
 *  reason; a full device as such; anything else `fallback` (its raw text goes to the console). */
export function plainError(err: unknown, fallback: string): string {
  if (err instanceof SayError) return err.message;
  if (err instanceof FetchFailed) return `${fallback} (${failReasonWords(err.reason)})`;
  if (err instanceof Error && err.name === 'QuotaExceededError') return 'this device is out of storage';
  console.warn(`plain words for: ${err instanceof Error ? `${err.name}: ${err.message}` : String(err)}`);
  return fallback;
}

/** A read that FAILED (as opposed to one that found nothing): network error, timeout, 5xx, a corrupt
 *  body, or — where the contract says the resource must exist — a 404. */
export class FetchFailed extends Error {
  readonly reason: FetchFailReason;
  constructor(reason: FetchFailReason, message: string) {
    super(message);
    this.name = 'FetchFailed';
    this.reason = reason;
  }
}

export interface BodyResult {
  status: number;
  ok: boolean;
  bytes: Uint8Array | null; // the full body when ok; null otherwise (the body is not read)
}

/** GET (or `init`) `url` and read the whole body under `d`. Resolves with the status for any HTTP
 *  answer (the caller decides what a 404 means); rejects with FetchFailed on network error/timeout,
 *  or with the caller's abort reason when `signal` aborts. */
export async function fetchBody(url: string, signal: AbortSignal | undefined, d: Deadline = TILE_DEADLINE, init?: RequestInit): Promise<BodyResult> {
  if (signal?.aborted) throw signal.reason ?? new DOMException('aborted', 'AbortError');
  const ctl = new AbortController();
  let timedOut: string | null = null;
  const onAbort = (): void => ctl.abort(signal?.reason);
  signal?.addEventListener('abort', onAbort, { once: true });
  let phase: ReturnType<typeof setTimeout> | undefined;
  const arm = (ms: number, what: string): void => {
    clearTimeout(phase);
    phase = setTimeout(() => {
      timedOut = what;
      ctl.abort();
    }, ms);
  };
  const total = setTimeout(() => {
    timedOut = `not finished in ${d.totalMs / 1000} s`;
    ctl.abort();
  }, d.totalMs);
  const fail = (err: unknown): never => {
    if (signal?.aborted) throw err;
    if (timedOut) throw new FetchFailed('timeout', `timed out (${timedOut})`);
    throw new FetchFailed('network', 'network error');
  };
  try {
    arm(d.firstByteMs, `no response in ${d.firstByteMs / 1000} s`);
    let res: Response;
    try {
      res = await fetch(url, { ...init, signal: ctl.signal });
    } catch (err) {
      return fail(err);
    }
    if (!res.ok) {
      res.body?.cancel().catch(() => {});
      return { status: res.status, ok: false, bytes: null };
    }
    const reader = res.body?.getReader();
    if (!reader) {
      arm(d.idleMs, `body stalled for ${d.idleMs / 1000} s`);
      try {
        return { status: res.status, ok: true, bytes: new Uint8Array(await res.arrayBuffer()) };
      } catch (err) {
        return fail(err);
      }
    }
    const chunks: Uint8Array[] = [];
    let n = 0;
    for (;;) {
      arm(d.idleMs, `body stalled for ${d.idleMs / 1000} s`);
      let r: ReadableStreamReadResult<Uint8Array>;
      try {
        r = await reader.read();
      } catch (err) {
        return fail(err);
      }
      if (r.done) break;
      chunks.push(r.value);
      n += r.value.byteLength;
    }
    const bytes = new Uint8Array(n);
    let o = 0;
    for (const c of chunks) {
      bytes.set(c, o);
      o += c.byteLength;
    }
    return { status: res.status, ok: true, bytes };
  } finally {
    clearTimeout(phase);
    clearTimeout(total);
    signal?.removeEventListener('abort', onAbort);
  }
}

/** Run `read` under a deadline: the signal it is handed aborts after `ms` (it then rejects with a
 *  FetchFailed 'timeout', whatever fetch() or the body read threw) or when `parent` aborts (passed
 *  through unchanged). The timer and the listener on `parent` are released the moment `read` settles,
 *  so a fast read leaves nothing armed behind it. For reads through a helper that takes only a signal
 *  (dataFetch): read the body inside `read`, so the deadline covers it. */
export async function withDeadline<T>(ms: number, parent: AbortSignal | undefined, read: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const ctl = new AbortController();
  const onAbort = (): void => ctl.abort(parent?.reason);
  if (parent?.aborted) ctl.abort(parent.reason);
  else parent?.addEventListener('abort', onAbort, { once: true });
  const timer = setTimeout(() => ctl.abort(new FetchFailed('timeout', `timed out after ${ms / 1000} s`)), ms);
  try {
    return await read(ctl.signal);
  } catch (err) {
    if (ctl.signal.aborted && !parent?.aborted && ctl.signal.reason instanceof FetchFailed) throw ctl.signal.reason;
    throw err;
  } finally {
    clearTimeout(timer);
    parent?.removeEventListener('abort', onAbort);
  }
}
